import { useEffect, useState } from 'react';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const textColor = isScrolled ? '#1A1917' : 'rgba(255,255,255,0.85)';
  const hoverBorderColor = isScrolled ? '#1A1917' : 'rgba(201,169,110,0.8)';

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between transition-all duration-500"
        style={{
          height: isScrolled ? '80px' : '100px',
          paddingLeft: window.innerWidth <= 768 ? '20px' : '80px',
          paddingRight: window.innerWidth <= 768 ? '20px' : '80px',
          backgroundColor: isScrolled ? 'rgba(248,246,243,0.92)' : 'transparent',
          borderBottom: isScrolled ? '1px solid rgba(58,61,66,0.2)' : '1px solid transparent',
          backdropFilter: isScrolled ? 'blur(16px)' : 'none',
        }}
      >
        {/* Logo / Wordmark */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-3 cursor-pointer"
          style={{ background: 'none', border: 'none', padding: 0 }}
        >
          {/* Micro crest */}
          <div
            className="relative flex items-center justify-center flex-shrink-0"
            style={{ width: window.innerWidth <= 768 ? '22px' : '28px', height: window.innerWidth <= 768 ? '22px' : '28px' }}
          >
            <div
              className="absolute inset-0 border"
              style={{
                borderColor: isScrolled ? 'rgba(201,169,110,0.5)' : 'rgba(201,169,110,0.45)',
                borderRadius: '1px',
                transform: 'rotate(45deg)',
              }}
            />
            <div
              className="absolute border"
              style={{
                inset: '5px',
                borderColor: isScrolled ? 'rgba(201,169,110,0.25)' : 'rgba(201,169,110,0.2)',
                borderRadius: '1px',
                transform: 'rotate(45deg)',
              }}
            />
            <div
              className="absolute rounded-full"
              style={{
                width: window.innerWidth <= 768 ? '4px' : '5px',
                height: window.innerWidth <= 768 ? '4px' : '5px',
                backgroundColor: '#C9A96E',
                boxShadow: '0 0 6px rgba(201,169,110,0.5)',
              }}
            />
          </div>

          {/* Wordmark */}
          <div className="flex flex-col items-start">
            <span
              className="font-serif transition-all duration-500"
              style={{
                fontSize: window.innerWidth <= 768 ? (isScrolled ? '16px' : '18px') : (isScrolled ? '20px' : '24px'),
                fontFamily: '"Playfair Display", Georgia, serif',
                letterSpacing: '0.18em',
                color: isScrolled ? '#1A1917' : '#FFFFFF',
                lineHeight: 1,
              }}
            >
              ANTEROOM
            </span>
            <span
              className="font-sans uppercase transition-colors duration-500"
              style={{
                fontSize: window.innerWidth <= 768 ? '6px' : '7px',
                letterSpacing: '0.38em',
                color: '#C9A96E',
                marginTop: '4px',
                opacity: isScrolled ? 0.9 : 0.8,
              }}
            >
              Sovereign Advisory
            </span>
          </div>
        </button>

        {/* Desktop Nav links */}
        <div className="hidden md:flex items-center gap-[52px]">
          {[
            { label: 'Disciplines', id: 'services' },
            { label: 'Mandate', id: 'about' },
            { label: 'Clientele', id: 'who-we-serve' },
            { label: 'Contact', id: 'contact' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="font-sans uppercase transition-all duration-400 whitespace-nowrap cursor-pointer pb-[2px]"
              style={{
                fontSize: '10px',
                letterSpacing: '0.12em',
                color: textColor,
                background: 'none',
                border: 'none',
                borderBottom: `1px solid transparent`,
                padding: '2px 0',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderBottomColor = hoverBorderColor;
                (e.currentTarget as HTMLButtonElement).style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderBottomColor = 'transparent';
                (e.currentTarget as HTMLButtonElement).style.opacity = '1';
              }}
            >
              {item.label}
            </button>
          ))}

          {/* CTA button */}
          <button
            onClick={() => scrollToSection('contact')}
            className="font-sans uppercase transition-all duration-500 whitespace-nowrap cursor-pointer"
            style={{
              fontSize: '9px',
              letterSpacing: '0.22em',
              color: isScrolled ? '#0D0C0A' : '#C9A96E',
              border: `1px solid ${isScrolled ? 'rgba(26,25,23,0.4)' : 'rgba(201,169,110,0.5)'}`,
              padding: '10px 24px',
              background: 'transparent',
            }}
            onMouseEnter={(e) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.backgroundColor = isScrolled ? '#0D0C0A' : '#C9A96E';
              btn.style.color = isScrolled ? '#F8F6F3' : '#0D0C0A';
            }}
            onMouseLeave={(e) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.backgroundColor = 'transparent';
              btn.style.color = isScrolled ? '#0D0C0A' : '#C9A96E';
            }}
          >
            Consult
          </button>
        </div>

        {/* Mobile Hamburger Icon */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden flex flex-col justify-center items-center w-8 h-8 cursor-pointer"
          style={{ background: 'none', border: 'none', padding: 0 }}
          aria-label="Toggle menu"
        >
          <span
            className="block transition-all duration-300"
            style={{
              width: '24px',
              height: '2px',
              backgroundColor: isScrolled ? '#1A1917' : '#FFFFFF',
              marginBottom: '5px',
              transform: isMobileMenuOpen ? 'rotate(45deg) translateY(7px)' : 'none',
            }}
          />
          <span
            className="block transition-all duration-300"
            style={{
              width: '24px',
              height: '2px',
              backgroundColor: isScrolled ? '#1A1917' : '#FFFFFF',
              marginBottom: '5px',
              opacity: isMobileMenuOpen ? 0 : 1,
            }}
          />
          <span
            className="block transition-all duration-300"
            style={{
              width: '24px',
              height: '2px',
              backgroundColor: isScrolled ? '#1A1917' : '#FFFFFF',
              transform: isMobileMenuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
            }}
          />
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className="md:hidden fixed inset-0 z-40 transition-all duration-500"
        style={{
          backgroundColor: 'rgba(13,12,10,0.98)',
          backdropFilter: 'blur(20px)',
          opacity: isMobileMenuOpen ? 1 : 0,
          pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
          transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        <div className="flex flex-col items-center justify-center h-full px-8">
          {/* Mobile Menu Links */}
          <div className="flex flex-col items-center gap-10 mb-12">
            {[
              { label: 'Disciplines', id: 'services' },
              { label: 'Mandate', id: 'about' },
              { label: 'Clientele', id: 'who-we-serve' },
              { label: 'Contact', id: 'contact' },
            ].map((item, index) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="font-sans uppercase transition-all duration-400 cursor-pointer"
                style={{
                  fontSize: '14px',
                  letterSpacing: '0.18em',
                  color: 'rgba(255,255,255,0.9)',
                  background: 'none',
                  border: 'none',
                  padding: '8px 0',
                  borderBottom: '1px solid transparent',
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: isMobileMenuOpen ? `${index * 0.1}s` : '0s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderBottomColor = '#C9A96E';
                  (e.currentTarget as HTMLButtonElement).style.color = '#C9A96E';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderBottomColor = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.9)';
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Mobile CTA Button */}
          <button
            onClick={() => scrollToSection('contact')}
            className="font-sans uppercase transition-all duration-500 whitespace-nowrap cursor-pointer"
            style={{
              fontSize: '11px',
              letterSpacing: '0.22em',
              color: '#C9A96E',
              border: '1px solid rgba(201,169,110,0.5)',
              padding: '14px 40px',
              background: 'transparent',
              opacity: isMobileMenuOpen ? 1 : 0,
              transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: isMobileMenuOpen ? '0.4s' : '0s',
            }}
            onMouseEnter={(e) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.backgroundColor = '#C9A96E';
              btn.style.color = '#0D0C0A';
            }}
            onMouseLeave={(e) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.backgroundColor = 'transparent';
              btn.style.color = '#C9A96E';
            }}
          >
            Consult
          </button>

          {/* Mobile Menu Footer */}
          <div
            className="absolute bottom-12 flex flex-col items-center gap-3"
            style={{
              opacity: isMobileMenuOpen ? 0.6 : 0,
              transitionDelay: isMobileMenuOpen ? '0.5s' : '0s',
            }}
          >
            <span
              className="font-sans uppercase"
              style={{
                fontSize: '8px',
                letterSpacing: '0.3em',
                color: '#C9A96E',
              }}
            >
              Nairobi · New York · Washington DC
            </span>
          </div>
        </div>
      </div>
    </>
  );
}